import { Component, Input, OnInit, OnChanges, SimpleChanges, inject, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterLink, Router } from '@angular/router';

import {
  FriendRequestService,
  IFriendListItem,
} from '../../shared/services/friend-request.service';
import { AuthStateService } from '../../shared/states/auth.state';
import { ConfirmationPopup } from '../../components/ui/confirmation-popup/confirmation-popup';
import { FriendRequestLimitPopup } from '../../components/ui/friend-request-limit-popup/friend-request-limit-popup';

const PAGE_SIZE = 24;

/**
 * Friends tab on the profile page ( /profile/:userName/friend ).
 * Shows the profile owner's friends, each annotated with the *viewer's own*
 * relationship to that person (NONE / PENDING_SENT / PENDING_RECEIVED /
 * FRIENDS), so every card gets the right action button — same idea as the
 * reaction/follow buttons on post cards.
 */
@Component({
  selector: 'app-friends-list',
  standalone: true,
  imports: [CommonModule, RouterLink, ConfirmationPopup, FriendRequestLimitPopup],
  templateUrl: './friends-list.html',
})
export class FriendsList implements OnInit, OnChanges {
  /** Profile owner's userName from the parent (profile.ts) */
  @Input() userName = '';

  private friendRequestService = inject(FriendRequestService);
  private authState = inject(AuthStateService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);

  items: IFriendListItem[] = [];
  isLoading = false;
  allLoaded = false;
  error: string | null = null;
  private page = 1;
  private total = 0;

  /** Tracks in-flight action per card (by user id) so its button can show a
   *  spinner/disabled state without blocking the rest of the list. */
  actionPendingFor: string | null = null;

  showFriendLimitPopup = false;
  friendLimitHours: number | null = null;

  showLoginPrompt = false;
  loginPromptUserName = '';
  loginPromptAction = '';

  get isOwnProfile(): boolean {
    const auth = this.authState.getAuthSnapshot();
    return !!(auth?.isLoggedIn && auth?.user?.userName && auth.user.userName === this.userName);
  }

  private isLoggedIn(): boolean {
    return this.authState.getAuthSnapshot()?.isLoggedIn === true;
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) this.resetAndLoad();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userName'] && !changes['userName'].firstChange) {
      this.resetAndLoad();
    }
  }

  private resetAndLoad(): void {
    this.items = [];
    this.page = 1;
    this.total = 0;
    this.allLoaded = false;
    this.error = null;
    this.loadNextPage();
  }

  loadNextPage(): void {
    if (!this.userName || this.isLoading || this.allLoaded) return;
    this.isLoading = true;
    this.error = null;
    this.cdr.detectChanges();

    this.friendRequestService.getFriendsByUserName(this.userName, this.page, PAGE_SIZE).subscribe({
      next: (res) => {
        const data = res?.data;
        const newItems = data?.items ?? [];
        this.items = [...this.items, ...newItems];
        this.total = data?.total ?? this.items.length;
        this.page += 1;
        this.isLoading = false;
        this.allLoaded = this.items.length >= this.total || newItems.length === 0;
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = 'Failed to load friends. Please try again.';
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  trackById(_: number, item: IFriendListItem): string {
    return item._id;
  }

  /** Single source of truth for the ui-avatars fallback used across this page. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  private requireLogin(userName: string, action: string): boolean {
    if (this.isLoggedIn()) return true;
    this.loginPromptUserName = userName;
    this.loginPromptAction = action;
    this.showLoginPrompt = true;
    this.cdr.detectChanges();
    return false;
  }

  closeLoginPrompt(): void {
    this.showLoginPrompt = false;
    this.cdr.detectChanges();
  }

  goToLogin(): void {
    this.closeLoginPrompt();
    this.router.navigate(['/login']);
  }

  addFriend(item: IFriendListItem, event: Event): void {
    event.stopPropagation();
    if (!this.requireLogin(item.userName, 'add as a friend')) return;

    const previous = item.friendshipStatus;
    item.friendshipStatus = 'PENDING_SENT';
    this.actionPendingFor = item._id;
    this.cdr.detectChanges();

    this.friendRequestService.send(item._id).subscribe({
      next: (res) => {
        item.friendshipRequestId = res?.data?._id;
        this.actionPendingFor = null;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        item.friendshipStatus = previous;
        this.actionPendingFor = null;
        const cooldownHours = this.friendRequestService.parseCooldownError(err);
        if (cooldownHours !== null) {
          this.friendLimitHours = cooldownHours;
          this.showFriendLimitPopup = true;
        }
        this.cdr.detectChanges();
      },
    });
  }

  cancelRequest(item: IFriendListItem, event: Event): void {
    event.stopPropagation();
    if (!item.friendshipRequestId) return;

    const previous = item.friendshipStatus;
    const prevRequestId = item.friendshipRequestId;
    item.friendshipStatus = 'NONE';
    this.actionPendingFor = item._id;
    this.cdr.detectChanges();

    this.friendRequestService.cancel(prevRequestId, item._id).subscribe({
      next: () => { this.actionPendingFor = null; this.cdr.detectChanges(); },
      error: () => {
        item.friendshipStatus = previous;
        item.friendshipRequestId = prevRequestId;
        this.actionPendingFor = null;
        this.cdr.detectChanges();
      },
    });
  }

  acceptRequest(item: IFriendListItem, event: Event): void {
    event.stopPropagation();
    if (!item.friendshipRequestId) return;

    const previous = item.friendshipStatus;
    const prevRequestId = item.friendshipRequestId;
    item.friendshipStatus = 'FRIENDS';
    this.actionPendingFor = item._id;
    this.cdr.detectChanges();

    this.friendRequestService.accept(prevRequestId, item._id).subscribe({
      next: () => { this.actionPendingFor = null; this.cdr.detectChanges(); },
      error: () => {
        item.friendshipStatus = previous;
        item.friendshipRequestId = prevRequestId;
        this.actionPendingFor = null;
        this.cdr.detectChanges();
      },
    });
  }

  declineRequest(item: IFriendListItem, event: Event): void {
    event.stopPropagation();
    if (!item.friendshipRequestId) return;

    const previous = item.friendshipStatus;
    const prevRequestId = item.friendshipRequestId;
    item.friendshipStatus = 'NONE';
    this.actionPendingFor = item._id;
    this.cdr.detectChanges();

    this.friendRequestService.decline(prevRequestId, item._id).subscribe({
      next: () => { this.actionPendingFor = null; this.cdr.detectChanges(); },
      error: () => {
        item.friendshipStatus = previous;
        item.friendshipRequestId = prevRequestId;
        this.actionPendingFor = null;
        this.cdr.detectChanges();
      },
    });
  }

  showUnfriendConfirm = false;
  private pendingUnfriendAction: (() => void) | null = null;

  promptUnfriend(item: IFriendListItem, event: Event): void {
    event.stopPropagation();
    this.pendingUnfriendAction = () => this.unfriendUser(item, event);
    this.showUnfriendConfirm = true;
  }

  confirmUnfriend(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction?.();
    this.pendingUnfriendAction = null;
  }

  cancelUnfriendConfirm(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction = null;
  }

  private unfriendUser(item: IFriendListItem, event: Event): void {
    event.stopPropagation();

    const previous = item.friendshipStatus;
    this.actionPendingFor = item._id;
    // Own friends list: remove the card entirely once unfriended.
    // Someone else's friends list: just flip the button back to "Add Friend".
    const removeOnSuccess = this.isOwnProfile;
    item.friendshipStatus = 'NONE';
    this.cdr.detectChanges();

    this.friendRequestService.unfriend(item._id).subscribe({
      next: () => {
        this.actionPendingFor = null;
        if (removeOnSuccess) {
          this.items = this.items.filter(i => i._id !== item._id);
          this.total = Math.max(0, this.total - 1);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        item.friendshipStatus = previous;
        this.actionPendingFor = null;
        this.cdr.detectChanges();
      },
    });
  }
}